import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import './PromoCodeForm.css';

function PromoCodeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [isActive, setIsActive] = useState(1);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchPromoCode = useCallback(async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/promo-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const promo = response.data;
      setCode(promo.code || '');
      setDiscountPercentage(promo.discount_percentage || '');
      setExpirationDate(promo.expiration_date || '');
      setIsActive(promo.is_active || 1);
    } catch (err) {
      setError('Failed to fetch promo code.');
    }
  }, [id]);

  useEffect(() => {
    fetchPromoCode();
  }, [fetchPromoCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSaving(true);

    const data = {
      code: code.trim().toUpperCase(),
      discount_percentage: discountPercentage,
      expiration_date: expirationDate,
      is_active: Number(isActive),
    };

    try {
      const token = localStorage.getItem('adminToken');
      if (id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/promo-codes/${id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/promo-codes`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save promo code.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-form-page page-shell">
      <div className="admin-form-shell">
        <div className="admin-form-header">
          <div>
            <span className="tag">Admin Pricing</span>
            <h1 className="page-title">{id ? 'Edit promo code' : 'Add promo code'}</h1>
            <p className="page-lead">
              Manage promotional pricing without breaking consistency across checkout and catalog pages.
            </p>
          </div>
          <Link to="/admin" className="ghost-cta">Back to Admin</Link>
        </div>

        <div className="admin-form-layout promo-layout">
          <form className="section-card admin-form-card" onSubmit={handleSubmit}>
            {error && <p className="error-text">{error}</p>}

            <label className="admin-field">
              <span>Promo code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                required
                placeholder="WELCOME10"
              />
            </label>

            <div className="form-grid two-column">
              <label className="admin-field">
                <span>Discount percentage</span>
                <input
                  type="number"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(e.target.value)}
                  required
                  min="1"
                  max="100"
                  placeholder="10"
                />
              </label>

              <label className="admin-field">
                <span>Expiration date</span>
                <input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  required
                />
              </label>
            </div>

            <label className="admin-field">
              <span>Status</span>
              <select value={isActive} onChange={(e) => setIsActive(e.target.value)}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </label>

            <div className="form-actions">
              <button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Promo Code'}
              </button>
              <Link to="/admin" className="ghost-cta">Cancel</Link>
            </div>
          </form>

          <aside className="section-card admin-form-sidecard">
            <h2>Promo guidance</h2>
            <ul className="admin-hint-list">
              <li>Keep codes short and uppercase so they are easy to share and support.</li>
              <li>Use clean percentages only. If you need tiered pricing later, that should be a separate pricing model.</li>
              <li>Inactive codes stay in the system for reporting but no longer validate at checkout.</li>
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default PromoCodeForm;
